/**
 * Time utility class for 24-hour time representation and manipulation.
 *
 * - Always normalized to valid 24-hour time (00:00:00 to 23:59:59).
 * - Supports parsing from string, adding hours/minutes, and formatting.
 *
 * Example usage:
 *   const t = new Time('25:75:75'); // normalized to 02:16:15
 *   t.addMinutes(10).toString();    // '02:26:15'
 *   Time.format(t, 'mm:ss');        // '16:15'
 */
export class Time {
  /** Hours (0-23) */
  private readonly hours: number;
  /** Minutes (0-59) */
  private readonly minutes: number;
  /** Seconds (0-59) */
  private readonly seconds: number;

  /**
   * Construct a Time instance from a string in 'hh:mm:ss', 'hh:mm', or 'hh' format.
   * Any overflow or negative values are normalized to valid 24-hour time.
   * @param timeString Time string (e.g. '25:75:75', '13:05', '7')
   */
  constructor(timeString: string | Time | Object) {
    let hours = 0;
    let minutes = 0;
    let seconds = 0;

    if (typeof timeString === 'string') {
      // Support for 12-hour (am/pm) and 24-hour formats
      const ampmMatch = timeString.trim().match(/(am|pm)$/i);
      let isPM = false;
      let isAMPM = false;
      let timePart = timeString.trim();
      if (ampmMatch) {
        isAMPM = true;
        isPM = ampmMatch[1].toLowerCase() === 'pm';
        timePart = timePart.replace(/\s*(am|pm)$/i, '');
      }
      // Handle empty string
      if (timePart === '') {
        hours = 0;
        minutes = 0;
        seconds = 0;
      } else {
        const parts = timePart.split(':').map(Number);
        hours = Number.isNaN(parts[0]) ? 0 : parts[0];
        minutes = Number.isNaN(parts[1]) ? 0 : parts.length > 1 ? parts[1] : 0;
        seconds = Number.isNaN(parts[2]) ? 0 : parts.length > 2 ? parts[2] : 0;
      }
      if (isAMPM) {
        // 12-hour to 24-hour conversion
        if (hours === 12) {
          hours = isPM ? 12 : 0;
        } else {
          hours = isPM ? hours + 12 : hours;
        }
      }
    } else if (
      timeString instanceof Time ||
      (typeof timeString === 'object' &&
        timeString.hasOwnProperty('hours') &&
        (timeString as Object).hasOwnProperty('minutes') &&
        (timeString as Object).hasOwnProperty('seconds'))
    ) {
      const time = timeString as Time;
      hours = time.hours;
      minutes = time.minutes;
      seconds = time.seconds;
    }

    // Normalize seconds and minutes
    minutes += Math.floor(seconds / 60);
    seconds = ((seconds % 60) + 60) % 60;
    hours += Math.floor(minutes / 60);
    minutes = ((minutes % 60) + 60) % 60;
    hours = ((hours % 24) + 24) % 24;

    this.hours = hours;
    this.minutes = minutes;
    this.seconds = seconds;
  }

  /**
   * Returns a new Time with the given number of minutes added (can be negative).
   * @param minutesToAdd Minutes to add
   * @returns New Time instance
   */
  public addMinutes(minutesToAdd: number): Time {
    // Calculate total seconds from midnight, add minutes, then normalize
    let totalSeconds = this.hours * 3600 + this.minutes * 60 + this.seconds + minutesToAdd * 60;
    // Normalize to 0..86399 (seconds in a day)
    totalSeconds = ((totalSeconds % 86400) + 86400) % 86400;
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return new Time(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
  }

  /**
   * Returns a new Time with the given number of hours added (can be negative).
   * @param hoursToAdd Hours to add
   * @returns New Time instance
   */
  public addHours(hoursToAdd: number): Time {
    // Calculate total seconds from midnight, add hours, then normalize
    let totalSeconds = this.hours * 3600 + this.minutes * 60 + this.seconds + hoursToAdd * 3600;
    totalSeconds = ((totalSeconds % 86400) + 86400) % 86400;
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return new Time(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
  }

  /**
   * Returns the time as a zero-padded string in 'hh:mm:ss' format.
   * @returns Time string
   */
  public toString(): string {
    return `${String(this.hours).padStart(2, '0')}:${String(this.minutes).padStart(2, '0')}:${String(this.seconds).padStart(2, '0')}`;
  }

  /**
   * Formats the time using a custom format string.
   * Supported tokens: 'hh', 'mm', 'ss', 'h', 'a' (am/pm), 'A' (AM/PM).
   * Example: 'mm:ss', 'hh:mm', 'h:mm a', 'hh:mm:ss A', 'h:mm'.
   * @param time Time instance
   * @param format Format string (default 'hh:mm:ss')
   * @returns Formatted time string
   */
  static format(time: Time, format: string = 'hh:mm:ss'): string {
    const hours24 = time.hours;
    const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
    const minutes = String(time.minutes).padStart(2, '0');
    const seconds = String(time.seconds).padStart(2, '0');
    const ampm = hours24 < 12 ? 'am' : 'pm';
    const AMPM = ampm.toUpperCase();
    // Replace longer tokens first, then 'h' only if not followed by a letter
    let result = format;
    result = result.replace(/hh/g, String(hours24).padStart(2, '0'));
    result = result.replace(/mm/g, minutes);
    result = result.replace(/ss/g, seconds);
    result = result.replace(/A/g, AMPM);
    result = result.replace(/a/g, ampm);
    // Replace 'h' only if not followed by a letter (so not in 'hours')
    result = result.replace(/h(?![a-zA-Z])/g, String(hours12));
    return result;
  }

  /**
   * Compares this Time instance to another for equality (hh:mm:ss).
   * @param other Time instance to compare
   * @returns true if hours, minutes, and seconds are equal
   */
  public isEqual(other: Time): boolean {
    return this.hours === other.hours && this.minutes === other.minutes && this.seconds === other.seconds;
  }

  public static diffInMinutes(startTime: Time, endTime: Time): number {
    if (startTime >= endTime) {
      return Math.abs(startTime.hours * 60 + startTime.minutes - (endTime.hours * 60 + endTime.minutes));
    }
    return Math.abs(endTime.hours * 60 + endTime.minutes - (startTime.hours * 60 + startTime.minutes));
  }
}
